import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { setFeatureFlags, setFeatureFlagsReady, selectFeatureFlagsReady, selectFeatureFlags } from '../../store/slices/feature-flags.slice';
import { OrganizationApi } from '../../api';
import { useContainer } from '../useContainer';
import { FeatureFlagsDto } from '../../commonTypes/organization.types';
import { LocalStorage, ACCESS_TOKEN } from '../../util/LocalStorage';
import { selectUserSub, selectOrganizationId, selectAuthInitialized } from '../../store/slices/auth.slice';

export const useFeatureFlag = (flagName: string): boolean => {
    const organizationApi: OrganizationApi = useContainer(OrganizationApi);
    const dispatch = useAppDispatch();
    const storage: LocalStorage = useContainer(LocalStorage);
    const flags = useAppSelector(selectFeatureFlags);
    const isReady = useAppSelector(selectFeatureFlagsReady);
    const userSub = useSelector<RootState, string>(selectUserSub);
    const organizationId = useSelector<RootState, string>(selectOrganizationId);
    const isAuthInitialized = useSelector<RootState, boolean>(selectAuthInitialized);

    useEffect(() => {
        if (!isReady) {
            const accessToken = storage.get(ACCESS_TOKEN);

            if (!isAuthInitialized || !userSub || !accessToken || !organizationId) {
                return;
            }

            const { exec, abort } = organizationApi.getFeatureFlags({
                onSuccess: res => {
                    dispatch(setFeatureFlags(res.body));
                    dispatch(setFeatureFlagsReady(true));
                },
                onError: error => {
                    console.error('Failed to fetch feature flags:', error);
                    dispatch(setFeatureFlagsReady(true));
                },
            });
            exec();
            return abort;
        }
    }, [isReady, isAuthInitialized, userSub, organizationId, dispatch]);

    return useMemo(() => {
        const flag = flags.find((f: FeatureFlagsDto) => f.name === flagName);
        return flag?.enabled ?? false;
    }, [flags, flagName]);
};
